<script setup lang="js">

import { onMounted, ref } from 'vue'
import { moodleApi } from '@/api/Moodle/MoodleApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import LoadingModal from '@/components/Modals/LoadingModal.vue'
import { useLearningCenterStore } from '@/stores/learningCenterStore.js'

const isLoading = ref(false)
const loadingMessage = ref('')
const courses = ref(null)
const toaster = new Toaster()
const learningCenterStore = useLearningCenterStore()

onMounted(() => {
	loadMyCourses()
})

function loadMyCourses() {
	const cachedMoodleCourses = learningCenterStore.getUserMoodleCourses

	if (cachedMoodleCourses) {
		courses.value = cachedMoodleCourses
	}
	else {
		loadingMessage.value = 'Loading your Moodle courses...'
		isLoading.value = true
	}

	moodleApi.getMyCourses((response) => {
		const courseList = response.body()?.courses
		const errorCode = response.body()?.errorcode

		if (courseList) {
			courses.value = courseList
			learningCenterStore.setUserMoodleCourses(courseList)
		}
		else if (errorCode) {
			toaster.error('Failed to load your courses, please try again.')
		}

		isLoading.value = false
	})
}

const openCourse = (courseId) => {
	const courseUrl = import.meta.env.VITE_MOODLE_BASE_URL + `/course/view.php?id=${courseId}`
	if (courseUrl) {
		loadingMessage.value = 'Opening Moodle course...'
		isLoading.value = true

		moodleApi.getAutoLoginData((autoLoginData) => {
			isLoading.value = false

			if (autoLoginData) {
				window.open(`${autoLoginData.loginurl}&wantsurl=${encodeURI(courseUrl)}`, '_blank', 'noopener,noreferrer')
			}
			else {
				window.open(courseUrl, '_blank', 'noopener,noreferrer');
			}
		}, () => {
			isLoading.value = false
			window.open(courseUrl, '_blank', 'noopener,noreferrer');
		})
	}
}

</script>

<template>
	<div class="mt-8 w-fit">
		<div v-if="courses">
			<div class="flex flex-col space-y-4">
				<span class="font-bold text-xl">ESRD Assignments and Resources</span>

				<div class="flex flex-wrap gap-4 mt-6">
					<div class="flex flex-col flex-none w-64 h-60 rounded-xl border border-neutral-subtle border-solid bg-neutral shadow-lg overflow-hidden cursor-pointer"
					     v-for="course in courses" :key="course.id" @click="openCourse(course.id)">
						<div class="w-full h-28">
							<img class="size-full object-cover" :src="course.courseimage">
						</div>
						<div class="flex flex-col flex-1 bg-neutral p-4 min-w-0">
							<span class="w-full text-primary font-semibold text-[15px] underline decoration-grayscale-border line-clamp-2">{{ course.fullname }}</span>
							<span class="mt-1 text-grayscale-body font-medium text-sm truncate">{{ course.coursecategory }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<span v-else>It looks like you have no Moodle courses available right now.</span>
	</div>

	<Teleport to="html">
		<LoadingModal v-if="isLoading" :message="loadingMessage"/>
	</Teleport>
</template>
